import { BID_TEMPLATES } from '@/pages/request/bidTemplates.ts'
import { Button, Form, Input, message, Select, Space, Spin, Switch, Tag, Tooltip } from 'antd'
import { sspApi } from 'common/api/entitiesApi.tsx'
import { AnchorsForm } from 'common/components/AnchorsForm'
import { FormCard } from 'common/components/FormCard'
import { SspSelector } from 'common/components/reloadable/SspSelector.tsx'
import { useAppContext } from 'common/context/AppContext.tsx'
import { apiCall } from 'common/util/Util.tsx'
import React, { useState } from 'react'

import AutoResizeMonaco from './AutoResizeMonaco'
import ResponsePreview from './ResponsePreview'

const VITE_BIDDER = import.meta.env.VITE_BIDDER_ENDPOINT
const VITE_PROTOCOL = import.meta.env.VITE_PROTOCOL

interface State {
  url: string
  source: string
  ssp: number | undefined
  sspEndpoint?: string
  type: 'banner' | 'video' | 'native' | 'audio'
  debug: boolean
  responseBody: string
  responseStatus?: number
  loadingInit: boolean
  loadingSend: boolean
  templateKey: string
  test: boolean
}

const baseWithoutTrailingSlash = (VITE_BIDDER || '').replace(/\/$/, '')

const buildUrl = (endpoint?: string, debug?: boolean, test?: boolean): string => {
  const path = '/bid' + (endpoint ? `/${endpoint}` : '')
  const searchParams = new URLSearchParams()
  if (debug) searchParams.set("trace", String(debug))
  if (test) searchParams.set("test", String(test))
  const query = searchParams.toString()
  return VITE_PROTOCOL + '//' + baseWithoutTrailingSlash + path + (query ? `?${query}` : '')
}

const statusTag = (status?: number) =>
  status === undefined ? null : status >= 200 && status < 300 ? (
    <Tag color="green" style={{ marginLeft: 10 }}>
      {status}
    </Tag>
  ) : status >= 400 ? (
    <Tag color="red">{status}</Tag>
  ) : (
    <Tag color="blue">{status}</Tag>
  )

const getTypeForTemplate = (key: string): State['type'] => {
  if (key === 'banner') return 'banner'
  if (key === 'video') return 'video'
  if (key === 'audio') return 'audio'
  return 'native'
}

const TestRequest: React.FC = () => {
  const [form] = Form.useForm()
  const appContext = useAppContext()
  const [endpointLoading, setEndpointLoading] = useState(false)

  const [state, setState] = useState<State>({
    url: buildUrl(undefined, false, false),
    source: BID_TEMPLATES[0].json,
    ssp: undefined,
    sspEndpoint: undefined,
    type: getTypeForTemplate(BID_TEMPLATES[0].key),
    debug: false,
    responseBody: '',
    responseStatus: undefined,
    loadingInit: false,
    loadingSend: false,
    templateKey: BID_TEMPLATES[0].key,
    test: false,
  })

  const fetchSspEndpoint = (sspId: number | undefined, debug: boolean) => {
    if (!sspId) {
      setState((prev) => ({
        ...prev,
        ssp: undefined,
        sspEndpoint: undefined,
        url: buildUrl(undefined, debug),
      }))
      return
    }
    setEndpointLoading(true)
    apiCall(
      setEndpointLoading,
      appContext,
      () => sspApi.ssp(sspId),
      (data) => {
        const endpoint = data?.data?.endpoint
        setState((prev) => ({
          ...prev,
          ssp: sspId,
          sspEndpoint: endpoint,
          url: buildUrl(endpoint, debug),
        }))
      },
    )
  }

  const refreshState = (newState: Partial<State>) => {
    setState((prevState) => ({ ...prevState, ...newState }))
  }

  const handleDebugSwitch = (checked: boolean) => {
    setState((prev) => ({
      ...prev,
      debug: checked,
      url: buildUrl(prev.sspEndpoint, checked, prev.test),
    }))
  }

  const handleTestSwitch = (checked: boolean) => {
    setState((prev) => ({
      ...prev,
      test: checked,
      url: buildUrl(prev.sspEndpoint, prev.debug, checked),
    }))
  }

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value.replace(/\s+/g, '')
    refreshState({
      url,
      debug: url.includes('trace=true'),
      test: url.includes('test=true')
    })
  }

  const handleSend = async () => {
    try {
      const requestURL = state.url
      const requestData = JSON.parse(state.source)
      setState((prev) => ({ ...prev, loadingSend: true, responseBody: '', responseStatus: undefined }))
      const resp = await fetch(requestURL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Rtb-Trace': 'true' },
        body: JSON.stringify(requestData),
      })
      const text = await resp.text()
      let json = ''
      try {
        json = JSON.stringify(JSON.parse(text), null, 2)
      } catch {
        json = text || ''
      }
      setState((prev) => ({
        ...prev,
        loadingSend: false,
        responseBody: json,
        responseStatus: resp.status,
      }))
    } catch (e: any) {
      setState((prev) => ({
        ...prev,
        loadingSend: false,
        responseBody: e.message || String(e),
        responseStatus: undefined,
      }))
      message.error('Request error: ' + (e.message || String(e)))
    }
  }

  const handleBeautify = () => {
    try {
      const obj = JSON.parse(state.source)
      refreshState({ source: JSON.stringify(obj, null, 2) })
    } catch {
      message.warning('Request body is not a valid JSON.')
    }
  }

  const handleTemplateSelect = (val: string) => {
    const found = BID_TEMPLATES.find((t) => t.key === val)
    if (found) {
      refreshState({
        source: found.json,
        type: getTypeForTemplate(found.key),
        templateKey: found.key,
      })
    }
  }

  const isLoading = state.loadingInit || endpointLoading

  return (
    <Spin spinning={isLoading || state.loadingSend}>
      <AnchorsForm form={form} onFinish={handleSend}>
        <FormCard id="general" title="Parameters">
          <Form.Item label="Endpoint URL">
            <Input value={state.url} placeholder="https://..." onChange={handleUrlChange} />
          </Form.Item>
          <Form.Item label="SSP">
            <SspSelector
              placeholder="Select SSP"
              value={state.ssp}
              onChange={(sspId: number | undefined) => {
                fetchSspEndpoint(sspId, state.debug)
              }}
            />
          </Form.Item>
        </FormCard>

        <FormCard
          id="request"
          title="Request (OpenRTB 2.6)"
          extra={
            <>
              <Switch
                checked={state.debug}
                onChange={handleDebugSwitch}
                style={{ marginLeft: 12, marginRight: 4 }}
                checkedChildren="debug"
                unCheckedChildren="debug"
              />
              <Switch
                  checked={state.test}
                  onChange={handleTestSwitch}
                  style={{ marginLeft: 12, marginRight: 4 }}
                  checkedChildren="test"
                  unCheckedChildren="test"
              />
            </>
          }
        >
          <Space style={{ marginBottom: 12 }}>
            <Select
              style={{ width: 180 }}
              placeholder="Templates"
              value={state.templateKey}
              onChange={handleTemplateSelect}
              options={BID_TEMPLATES.map(({ key, label }) => ({ value: key, label }))}
            />
            <Tooltip title="Beautify JSON">
              <Button onClick={handleBeautify}>Beautify</Button>
            </Tooltip>
            <Button type="primary" htmlType="submit" disabled={isLoading}>
              Send Request
            </Button>
          </Space>
          <AutoResizeMonaco value={state.source} onChange={(val) => refreshState({ source: val })} language="json" />
        </FormCard>

        {state.responseBody && <FormCard id="preview" title={<>Preview{state.responseStatus && statusTag(state.responseStatus)}</>}>
          <div style={{ minHeight: 500 }}>
            <ResponsePreview responseJson={state.responseBody} creativeType={state.type} />
          </div>
        </FormCard>}

        <FormCard id="response" title={<>Response{state.responseStatus && statusTag(state.responseStatus)}</>}>
         <AutoResizeMonaco value={state.responseBody} language="json" readOnly />
        </FormCard>
      </AnchorsForm>
    </Spin>
  )
}

export default TestRequest
